import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import StatsFilters from "./StatsFilters";
import StatsTable from "./StatsTable";
import { EMPTY_FILTERS, pill, rangeText, ready, toParams } from "@/lib/scoring";

function WebsiteCell({ r, admin, vis }) {
  return (
    <span data-testid={`lb-public-${r.id}`} className="text-[11px] font-medium text-slate-500">
      {r.hidden ? "Hidden from website" : r.consent ? "Shown on website" : "Not on website (no permission)"}
      {admin && <span className="block mt-0.5">
        <button data-testid={`lb-consent-${r.id}`} onClick={() => vis(r.id, { leaderboard_consent: !r.consent })} className="font-bold text-[#2563EB]">{r.consent ? "Remove permission" : "Permission given"}</button>
        <button data-testid={`lb-hide-${r.id}`} onClick={() => vis(r.id, { public_hidden: !r.hidden })} className="ml-2 font-bold text-slate-600">{r.hidden ? "Unhide" : "Hide"}</button>
      </span>}
    </span>
  );
}

export default function Leaderboards({ api, admin, onPlayer }) {
  const [disc, setDisc] = useState("hitting");
  const [f, setF] = useState({ ...EMPTY_FILTERS });
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [tick, setTick] = useState(0);
  const vis = async (id, body) => { await api.put(`/admin/players/${id}`, body); setTick((t) => t + 1); };
  useEffect(() => { api.get("/scoring/stats/filters", { discipline: disc }).then(setMeta); }, [api, disc]);
  useEffect(() => {
    if (!ready(f)) return;
    setErr("");
    const t = setTimeout(() => api.get("/scoring/stats/leaderboard", toParams(disc, f)).then(setData).catch((e) => setErr(e?.response?.data?.detail || "Could not load the leaderboard.")), 200);
    return () => clearTimeout(t);
  }, [api, disc, f, tick]);
  const label = rangeText(f, data?.range, data?.cycle_fallback);
  return (
    <div data-testid="leaderboards" className="space-y-4">
      <div className="flex gap-2">{["hitting", "pitching"].map((d) => <button key={d} data-testid={`lb-disc-${d}`} onClick={() => { setDisc(d); setData(null); setF((x) => ({ ...x, session_id: "" })); }} className={pill(disc === d)}>{d === "hitting" ? "Hitting" : "Pitching"}</button>)}</div>
      <StatsFilters f={f} setF={setF} meta={meta} idp="lb" />
      <p data-testid="lb-range-note" className="text-xs text-slate-500"><b className="text-[#0B0F19]">{label}</b> columns use only the selected dates. <b className="text-[#0B0F19]">Overall</b> columns are lifetime totals from every completed session. Averages = total points ÷ attempts.</p>
      {err && <p data-testid="lb-error" className="text-sm font-semibold text-red-600">{err}</p>}
      {!ready(f) && <p className="text-sm text-slate-500">Pick a session or both dates to see results.</p>}
      {ready(f) && !data && !err && <Loader2 className="animate-spin text-slate-400" />}
      {ready(f) && data && <StatsTable rows={data.rows} outcomes={data.outcomes} discipline={disc} rangeLabel={f.range === "all" ? "All Time" : "Selected"} onPlayer={(r) => onPlayer(r.id, disc)} idp="lb"
        extra={{ label: "Website", render: (r) => <WebsiteCell r={r} admin={admin} vis={vis} /> }} />}
      {data && <p className="text-xs text-slate-400">Ranked by average; players need {data.min_reps} attempts to be ranked. Click any column to sort, click again to reverse.</p>}
    </div>
  );
}
